import { useCallback, useEffect, useState } from 'react';
import PageWrapper from '../components/UI/PageWrapper';
import { ChatContainer } from '../nebula/components/chat/ChatContainer';
import { ConversationSidebar } from '../nebula/components/sidebar/ConversationSidebar';
import { clearNebulaToken } from '../nebula/lib/nebulaToken';
import { useArtist } from '../contexts/ArtistContext';
import { fetchConversations, deleteConversation } from '../nebula/lib/api';
import type { Conversation } from '../nebula/types';
import { trackEvent, NEBULA_EVENTS } from '../services/analytics';

const NebulaPage = () => {
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [chatKey, setChatKey] = useState(0);
  const { currentArtistId } = useArtist();

  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarMinimized, setSidebarMinimized] = useState(() => {
    // Load minimized preference from localStorage
    const saved = localStorage.getItem('nebula-sidebar-minimized');
    return saved === 'true';
  });
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [conversationsError, setConversationsError] = useState<string | null>(null);

  // Detect desktop for auto-opening sidebar
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);

  const handleConversationIdChange = useCallback((id: string) => {
    setConversationId(id);
  }, []);

  // Load conversations
  const loadConversations = useCallback(async () => {
    setConversationsLoading(true);
    setConversationsError(null);
    try {
      const response = await fetchConversations();
      setConversations(response.conversations);
    } catch (err) {
      setConversationsError(
        err instanceof Error ? err.message : 'Failed to load conversations'
      );
    } finally {
      setConversationsLoading(false);
    }
  }, []);

  // Handle conversation selection
  const handleSelectConversation = useCallback(
    (id: string) => {
      setConversationId(id);
      // ChatContainer will handle loading via conversationId prop change

      // Track conversation loaded
      trackEvent(NEBULA_EVENTS.NEBULA_CONVERSATION_LOADED);

      // Close sidebar on mobile after selection
      if (!isDesktop) {
        setSidebarOpen(false);
      }
    },
    [isDesktop]
  );

  // Handle new chat
  const handleNewChat = useCallback(() => {
    setConversationId(undefined);
    setChatKey((prev) => prev + 1); // Force ChatContainer remount

    // Track new chat started
    trackEvent(NEBULA_EVENTS.NEBULA_NEW_CHAT);

    // Close sidebar on mobile
    if (!isDesktop) {
      setSidebarOpen(false);
    }
  }, [isDesktop]);

  // Handle delete conversation
  const handleDeleteConversation = useCallback(
    async (id: string) => {
      try {
        await deleteConversation(id);

        // Track conversation deleted
        trackEvent(NEBULA_EVENTS.NEBULA_CONVERSATION_DELETED);

        // Refresh list
        await loadConversations();

        // If deleted current conversation, start new chat
        if (id === conversationId) {
          handleNewChat();
        }
      } catch (err) {
        console.error('Failed to delete conversation:', err);
        // Could show error toast here
      }
    },
    [conversationId, handleNewChat, loadConversations]
  );

  // Handle minimize/maximize sidebar
  const handleToggleMinimize = useCallback(() => {
    setSidebarMinimized((prev) => {
      const newValue = !prev;
      localStorage.setItem('nebula-sidebar-minimized', String(newValue));
      return newValue;
    });
  }, []);

  // Desktop detection
  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth >= 768;
      setIsDesktop(desktop);
      if (desktop) setSidebarOpen(true); // Auto-open on desktop
    };

    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load conversations on mount and artist change
  useEffect(() => {
    loadConversations();
  }, [loadConversations, currentArtistId]);

  useEffect(() => {
    clearNebulaToken();
    setConversationId(undefined);
    setChatKey((prev) => prev + 1);
  }, [currentArtistId]);

  useEffect(() => {
    const updateNavbarHeight = () => {
      const navbar = document.querySelector('header.navbar-menu-item') as HTMLElement | null;
      const height = navbar?.offsetHeight ?? 0;
      document.documentElement.style.setProperty('--nova-navbar-height', `${height}px`);
    };

    updateNavbarHeight();
    window.addEventListener('resize', updateNavbarHeight);
    return () => window.removeEventListener('resize', updateNavbarHeight);
  }, []);

  useEffect(() => {
    document.body.classList.add('nebula-page');
    return () => document.body.classList.remove('nebula-page');
  }, []);

  return (
    <PageWrapper className="h-full">
      <div className="nebula-root flex h-full bg-nova-dashboard-900">
        {/* Conversation Sidebar */}
        <ConversationSidebar
          isOpen={sidebarOpen || isDesktop}
          onClose={() => setSidebarOpen(false)}
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          sidebarOpen={sidebarOpen}
          isMinimized={sidebarMinimized}
          onToggleMinimize={handleToggleMinimize}
          conversations={conversations}
          selectedId={conversationId}
          onSelectConversation={handleSelectConversation}
          onNewChat={handleNewChat}
          onDeleteConversation={handleDeleteConversation}
          isLoading={conversationsLoading}
          error={conversationsError}
        />

        {/* Main Chat */}
        <main className="flex-1 overflow-hidden">
          <ChatContainer
            key={chatKey}
            conversationId={conversationId}
            onConversationIdChange={handleConversationIdChange}
            onConversationUpdate={loadConversations}
            conversationTitle={
              conversationId
                ? conversations.find((c) => c.id === conversationId)?.title
                : undefined
            }
          />
        </main>
      </div>
    </PageWrapper>
  );
};

export default NebulaPage;
